import { FC, useCallback, useEffect, useState } from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import type { GridApi } from "ag-grid-community";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { resourceDetailsTargetResourceSelector } from "@/store/resource-details/selectors/target-resource/resourceDetailsTargetResourceSelector";
import { getKubernetesLiveUsageMgmtPodsDataForDetailsDrawerThunk } from "@/store/kubernetes-live-usage-mgmt/thunks/resources/getKubernetesLiveUsageMgmtPodsDataForDetailsDrawerThunk";
import { ResourcesMasterGrid } from "@/components/pages/common/resources-ag-grid/ResourcesMasterGrid";
import { AgGridGlobalSearch } from "@/components/pages/common/ag-grid/components/global-search/AgGridGlobalSearch";
import { AGGridColumnsToolPanel } from "@/components/pages/common/ag-grid/components/AGGridColumnsToolPanel";
import { AllTypeResourcesData } from "@/store/resources-grid/utils/types/resources";

export const ResourceDetailsK8SPodsTabContent: FC = () => {
  const dispatch = useAppDispatch();

  const [pods, setPods] = useState<AllTypeResourcesData[]>([]);
  const [loading, setLoading] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const resource = useAppSelector(resourceDetailsTargetResourceSelector);
  const resourceName = resource?.name;
  const resourceId = resource?.provider_unique_identifier;
  const clusterId = resource?.accountId;
  const resourceType = resource?.resourceType;
  const podsLength = pods.length;

  const podsFetcher = useCallback(async () => {
    if (!resourceName || !resourceId || !resourceType || !clusterId) {
      return;
    }

    const pods = await dispatch(
      getKubernetesLiveUsageMgmtPodsDataForDetailsDrawerThunk({
        resourceName,
        resourceId,
        resourceType,
        clusterId,
      }),
    ).unwrap();

    if (pods) {
      setPods(pods.resources as AllTypeResourcesData[]);
    }
  }, [dispatch, resourceName, clusterId, resourceId, resourceType]);

  useEffect(() => {
    setLoading(true);
    podsFetcher().finally(() => setLoading(false));
  }, [podsFetcher]);

  if (!pods) {
    return null;
  }

  return (
    <Stack flexGrow={1} minHeight={200} p={1.5} height="100%">
      {loading && (
        <Box pb={2}>
          <LinearProgress />
        </Box>
      )}

      <Stack
        px={2}
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body2" fontWeight="medium">
          {!podsLength
            ? "No available Pods to show"
            : `Showing all ${podsLength} available Pods`}
        </Typography>

        <Stack alignItems="center" direction="row" spacing={1}>
          <AgGridGlobalSearch gridApi={gridApi} />

          <AGGridColumnsToolPanel gridApi={gridApi} />
        </Stack>
      </Stack>

      {resourceType && (
        <ResourcesMasterGrid
          hideRowActions
          hideSelection
          gridApi={gridApi}
          setGridApi={setGridApi}
          overrideGridData={pods}
          preventDetailsDrawerOpener
          resourceType="kubernetes_pod"
          masterResourceType={resourceType}
          agGridsWrapperStyles={agGridsWrapperStyles}
          masterGridId={`${resourceType}-nested-pod-master-grid-id`}
        />
      )}
    </Stack>
  );
};

const agGridsWrapperStyles = {
  border: 1,
  borderColor: "grey.300",
  borderRadius: 2,
  overflow: "hidden",
};
